import { observer } from "mobx-react-lite";
import React, { useState, useEffect } from "react";
import { ClientData, clientStatus } from "../../types/client";
import { useStore } from "../../app/stores/store";
import {
  Divider,
  Button,
  Upload,
  UploadFile,
  Modal,
  Form,
  Input,
  message,
} from "antd";
import {
  MailOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { allowedImageExtension } from "../../constants/constants";
import toast from "react-hot-toast";
import { BookingStatus } from "../../types/booking";
import { IMAGE_SERVER_PATH, PUBLIC_API_URL } from "../../environment";
import TextArea from "antd/es/input/TextArea";
import { FileResponseData } from "../../types/filesTypes";
import { FaWhatsapp } from "react-icons/fa";

type Props = {
  bookingId: string;
  setFlag: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  status: BookingStatus;
  phone: string;
  clients: ClientData[] | null;
  customerName: string | null;
};

const ClientsTable = ({
  clients,
  setFlag,
  email,
  status,
  phone,
  customerName,
  bookingId,
}: Props) => {
  const [allFilesUploaded, setAllFilesUploaded] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState<FileResponseData[]>([]);
  const [uploadingClients, setUploadingClients] = useState<Set<string>>(new Set()); // Track uploading clients
  const { clientStore } = useStore();
  const { getClientsByBookingId } = clientStore;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [form] = Form.useForm();
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);

  // --- New state for upload modal ---
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadClient, setUploadClient] = useState<ClientData | null>(null);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (bookingId) {
      getClientsByBookingId(bookingId);
    }
  }, [bookingId, getClientsByBookingId]);

  const getFirstAndLast = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1]}`;
  };


  useEffect(() => {
    if (isModalVisible && selectedClient) {

      // Reset form values with default subject/body personalized to customerName
      form.setFieldsValue({
        subject: "eVisa",
        body: `Dear ${getFirstAndLast(customerName ?? "")},

Please find the attached eVisa.

Best regards,
[Uber Travel Agency]`,
      });
    }
  }, [isModalVisible, selectedClient, customerName, form]);

  useEffect(() => {
    if (clients) {
      const allUploaded = clients.every(
        (client) =>
          (client.fileEntities && client.fileEntities.length > 0) ||
          client.status === clientStatus.Rejected
      );
      setAllFilesUploaded(allUploaded);
    }
  }, [clients]);

    const handleUploadChange = async (
    clientId: string,
    fileList: UploadFile<any>[]
  ) => {
    if (fileList.length > 0) {
      await handleFileUpload(clientId, fileList);
    }
  };

  const handleFileUpload = async (
    clientId: string,
    fileList: UploadFile<any>[]
  ) => {
    if (uploadingClients.has(clientId)) return;

    setUploadingClients((prev) => new Set(prev).add(clientId));

    const formData = new FormData();

    fileList.forEach((file) => {
      const nativeFile = file.originFileObj;
      if (nativeFile) {
        formData.append("Files", nativeFile);
      }
    });

    const isImage = fileList.some((file) =>
      allowedImageExtension.some((ext) =>
        file.originFileObj!.type.startsWith(ext)
      )
    );

    const directory = isImage ? "seed/image/" : "seed/files/";

    formData.append("directory", directory);
    formData.append("entityId", clientId);

    try {
      const result = await clientStore.uploadImage(formData);

      if (result.status === "success") {
        toast.success("Files uploaded successfully.");
        setFlag(true);
        // Refresh clients after upload
        await getClientsByBookingId(bookingId);
      } else {
        toast.error(`Upload failed: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error uploading files: ${error}`);
    } finally {
      setUploadingClients((prev) => {
        const updatedSet = new Set(prev);
        updatedSet.delete(clientId);
        return updatedSet;
      });
    }
  };



  const handleSendEmail = async (values: any, clientId: string) => {
    setSending(true);
    try {
      if (emailAttachments.length === 0) {
        toast.error("No files to send.");
        setSending(false);
        return;
      }

      // Use existing uploaded files only
      // Filter files with valid filePath
      const filesToSend = emailAttachments.filter(file => file.filePath);

      if (filesToSend.length === 0) {
        toast.error("No valid files to send.");
        setSending(false);
        return;
      }

      const emailBody = {
        email: email,
        subject: values.subject,
        body: values.body,
        files: filesToSend, // send all existing files here
      };

      const result = await clientStore.sendEmail(emailBody);

      if (result.status === "success") {
        toast.success("Email sent successfully");
        setAllFilesUploaded(false);
        setIsModalVisible(false);
        setSelectedClient(null);
        setEmailAttachments([]);
      } else {
        toast.error(`Error sending email: ${result.error}`);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(`Error sending email: ${error}`);
    } finally {
      setSending(false);
    }
  };
  const sendWhatsAppFromBackend = async (phone: string, client: ClientData) => {
    if (!client.bookingId) {
      toast.error("Invalid booking ID");
      return;
    }

    if (!client.fileEntities || client.fileEntities.length === 0) {
      toast.error("No files to send.");
      return;
    }

    const formattedPhone = phone.replace(/\D/g, "").startsWith("971")
      ? phone.replace(/\D/g, "")
      : `971${phone.replace(/\D/g, "")}`;

    const message = `Dear ${client.name},\nYour eVisa document is ready. Please find it attached.`;

    try {
      const response = await fetch(`${PUBLIC_API_URL}whatsapp/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone: formattedPhone,
          message,
          bookingId: client.bookingId,
          files: client.fileEntities,
        }),
      });

      if (response.ok) {
        toast.success("WhatsApp message sent!");
      } else {
        const error = await response.text();
        toast.error(`Error: ${error}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Network error while sending WhatsApp message");
    }
  };

  return (
    <div className="p-4 my- w-full border rounded-lg bg-gray-50">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">
        Clients Information
      </h2>
      <Divider />
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Passport Number</th>
              <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-center"> </th>
                              <th className="px-4 py-2 text-center">Actions</th>

                              <th className="px-4 py-2 text-center">Send</th>

                
            </tr>
          </thead>
          <tbody>
            {clients?.map((client, index) => (
              <tr key={client.id || index} className="border-b">
                <td className="px-4 py-2">{client.name}</td>
                <td className="px-4 py-2">{client.passportNumber}</td>
                <td className="px-4 py-2">
                  {client.status === clientStatus.Pending
                    ? "Pending"
                    : client.status === clientStatus.InProcess
                      ? "In Process"
                      : client.status === clientStatus.Accepted
                        ? "Accepted"
                        : "Rejected"}
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex justify-center space-x-2">
                  
                    {client.status === clientStatus.Pending && (
                      <>
                        <button
                          onClick={() => {
                            clientStore.setStatusInProcess(client.id);
                            setFlag(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg shadow hover:bg-green-600"
                        >
                          In-Process
                        </button>
                        <button
                          onClick={() => {
                            clientStore.setStatusRejected(client.id);
                            setFlag(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg shadow hover:bg-red-600"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {client.status === clientStatus.InProcess && (
                      <>
                        <button
                          onClick={() => {
                            clientStore.setStatusAccepted(client.id);
                            setFlag(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg shadow hover:bg-green-600"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => {
                            clientStore.setStatusRejected(client.id);
                            setFlag(true);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg shadow hover:bg-red-600"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </td>
 <td className="px-4 py-2 text-center">
        {client.fileEntities && client.fileEntities.length > 0 && (
          <Button
            type="default"
            onClick={() => {
              client.fileEntities?.forEach(file => {
                // Open each file in a new tab for download
                window.open(`${IMAGE_SERVER_PATH}/${file.filePath}`, "_blank");
              });
            }}
          >
            Download Files
          </Button>
        )}
      </td>
                <td className="px-4 py-2 text-center">
                  {client.status === clientStatus.Accepted && (
                    <div className="flex justify-center space-x-2">
                      <Button
                        type="default"
                        icon={<UploadOutlined />}
                        onClick={() => {
                          setUploadClient(client);
                          setUploadModalVisible(true);
                        }}
                      >
                        Upload Files
                      </Button>

                      <Button
                        type="primary"
                        icon={<MailOutlined />}
                        disabled={!client.fileEntities || client.fileEntities.length === 0}
                        onClick={() => {
                          setSelectedClient(client);
                          setEmailAttachments(client.fileEntities || []); // ✅ Set existing uploaded files
                          setIsModalVisible(true);
                        }}
                      >
                        Send Email
                      </Button>
                    </div>
                  )}
                </td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Upload Files Modal */}
      <Modal
        title={`Upload Files for ${uploadClient?.name ?? ""}`}
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setUploadClient(null);
          setUploadFileList([]);
        }}
        footer={null}
        destroyOnClose
      >
        <Upload
          multiple
          fileList={uploadFileList}
          onChange={({ fileList }) => setUploadFileList(fileList)}
          beforeUpload={() => false} // prevent auto upload
          accept=".pdf,.docx,.jpg,.png"
          listType="picture"
        >
          <Button icon={<UploadOutlined />}>Select Files</Button>
        </Upload>

        <div className="mt-4 flex justify-end space-x-2">
          <Button
            onClick={() => {
              setUploadModalVisible(false);
              setUploadClient(null);
              setUploadFileList([]);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={uploading}
            disabled={uploadFileList.length === 0}
            onClick={async () => {
              if (!uploadClient) return;
              setUploading(true);
              await handleFileUpload(uploadClient.id, uploadFileList);
              setUploading(false);
              setUploadModalVisible(false);
              setUploadClient(null);
              setUploadFileList([]);
            }}
          >
            Upload
          </Button>
        </div>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        title={`Send Email to ${selectedClient?.name ?? ""}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedClient(null);
          setEmailAttachments([]);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (selectedClient) {
              handleSendEmail(values, selectedClient.id);
            }
          }}
        >
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: "Please input the subject" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Body"
            name="body"
            rules={[{ required: true, message: "Please input the email body" }]}
          >
            <TextArea rows={5} />
          </Form.Item>

          <Form.Item label="Attachments">
            {emailAttachments.map((file) => {
              const fullUrl = `${IMAGE_SERVER_PATH.replace(/\/$/, '')}/${file.filePath?.replace(/^\//, '')}`;

              return (
                <a
                  key={file.fileName}
                  target="_blank"
                  href={fullUrl}
                  download={file.fileName}
                  rel="noopener noreferrer"
                >
                  <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center cursor-pointer mb-3">
                    {file?.fileName?.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                      <img
                        src={fullUrl}
                        alt={file.fileName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-600 text-3xl">📄</span>
                    )}
                  </div>
                </a>
              );
            })}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={sending}>
              Send Email
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default observer(ClientsTable);
